import { useMemo } from 'react'

import {
  type UseQueryResult,
  useQueries,
  useQuery,
} from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { buildPaginatedEndpoint, convertDatesInObject } from '@/helpers/api'

import {
  normalizeOrgIds,
  pursuitActiveAssignedQueryKeys,
} from '@/hooks/pursuits/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Pursuit } from '@/types/pursuit/Pursuit'

interface CommonProps<TResult> {
  selector?: (pursuits: Pursuit[]) => TResult
}

interface SingleOrgProps<TResult> extends CommonProps<TResult> {
  orgId: UUID | ''
  orgIds?: never
}

interface MultiOrgProps<TResult> extends CommonProps<TResult> {
  orgIds: UUID[]
  orgId?: never
}

type UseActiveAssignedPursuitsProps<TResult> =
  | SingleOrgProps<TResult>
  | MultiOrgProps<TResult>

interface UseActiveAssignedPursuitsResult<TResult> {
  data: TResult | undefined
  isLoading: boolean
  isError: boolean
  errors: Error[]
  failedOrgIds: UUID[]
}

const STALE_TIME_MS = 1000 * 60 * 5

const combineActiveAssignedPursuits = (
  results: UseQueryResult<PaginatedResponse<Pursuit>>[],
) => {
  const flat: Pursuit[] = []
  for (const r of results) {
    if (r.data) flat.push(...r.data.data)
  }
  return {
    flat,
    isLoading: results.some((r) => r.isLoading),
    isError: results.some((r) => r.isError),
    allUndefined: results.every((r) => r.data === undefined),
    errors: results
      .map((r) => r.error)
      .filter((e): e is Error => e instanceof Error),
    errorFlags: results.map((r) => r.isError),
  }
}

export const useActiveAssignedPursuits = <TResult = Pursuit[]>(
  args: UseActiveAssignedPursuitsProps<TResult>,
): UseActiveAssignedPursuitsResult<TResult> => {
  const { accessToken } = useAuthInfoWithThrow()
  const { selector } = args
  const orgIds = normalizeOrgIds(args)
  const isSingle = orgIds.length <= 1
  const singleOrgId = orgIds[0] ?? ''

  const singleQuery = useQuery({
    queryKey: pursuitActiveAssignedQueryKeys.organization({
      orgId: singleOrgId,
    }),
    queryFn: () =>
      fetchActiveAssignedPursuits(singleOrgId, accessToken, { limit: 10 }),
    staleTime: STALE_TIME_MS,
    enabled: isSingle && !!singleOrgId,
  })

  const multi = useQueries({
    queries: isSingle
      ? []
      : orgIds.map((orgId) => ({
          queryKey: pursuitActiveAssignedQueryKeys.organization({ orgId }),
          queryFn: () =>
            fetchActiveAssignedPursuits(orgId, accessToken, { limit: 10 }),
          staleTime: STALE_TIME_MS,
          enabled: !!orgId,
        })),
    combine: combineActiveAssignedPursuits,
  })

  const data = useMemo<TResult | undefined>(() => {
    if (isSingle) {
      if (singleQuery.data === undefined) return undefined
      const flat = singleQuery.data.data
      return selector ? selector(flat) : (flat as unknown as TResult)
    }
    if (multi.allUndefined) return undefined
    return selector ? selector(multi.flat) : (multi.flat as unknown as TResult)
  }, [isSingle, singleQuery.data, multi.allUndefined, multi.flat, selector])

  if (isSingle) {
    return {
      data,
      isLoading: singleQuery.isLoading,
      isError: singleQuery.isError,
      errors: singleQuery.error ? [singleQuery.error as Error] : [],
      failedOrgIds: singleQuery.isError && singleOrgId ? [singleOrgId] : [],
    }
  }

  return {
    data,
    isLoading: multi.isLoading,
    isError: multi.isError,
    errors: multi.errors,
    failedOrgIds: orgIds.filter((_, i) => multi.errorFlags[i]),
  }
}

export const fetchActiveAssignedPursuits = async (
  orgId: UUID | '',
  accessToken: string | undefined | null,
  pagination: PaginateRequest,
): Promise<PaginatedResponse<Pursuit>> => {
  if (accessToken === null || accessToken === undefined || !orgId) {
    return { count: 0, has_more: false, data: [] }
  }
  const url = buildPaginatedEndpoint(
    `${API_URL}/api/v1/organizations/${orgId}/pursuits/active/assigned`,
    pagination,
  )
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  return convertDatesInObject(await res.json()) as PaginatedResponse<Pursuit>
}
